import React, { useEffect, useState, useRef } from 'react';
import { CardElement, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { processOrder, applyCouponCode, removeCouponCode, updateNonce, sendSignUpEvent } from '../../services/api';
import { get_cookie } from '../../helper';
import { ExpressCheckoutElement } from '@stripe/react-stripe-js';
import Loader from '../loader/loader.component';
import styles from './paymentForm.module.scss'; // assuming you have styles defined
import * as Sentry from '@sentry/react';

const PaymentForm = ({
  cart,
  setCart,
  selectedPlan,
  loading,
  setLoading,
  currency_symbol,
  showSkeleton,
  setShowSkeleton,
  setProcessingOrder,
  initCart,
  signupEmailStep,
  userSubExists
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isCardElementVisible, setIsCardElementVisible] = useState(true); // To toggle the card input visibility
  const [coupon, setCoupon] = useState('');
  const [isGift, setIsGift] = useState(false); // To toggle the gift checkbox
  const [hasCoupon, setHasCoupon] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [checkErrors, setCheckErrors] = useState(false);
  const [subExists, setSubExists] = useState(userSubExists);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isExpressCheckoutVisible, setExpressCheckoutVisible] = useState(false);

  const [signupTriggered, setSignupTriggered] = useState(signupEmailStep);


  const expressElementRef = useRef(null);
  const [usePaymentElement, setUsePaymentElement] = useState(true); // Change this to true to show PaymentElement by default


  /**
   * If the subscription is a bogo, select the gift checkbox
   */
  useEffect(() => {
    const giftSelected  = selectedPlan?.bogo ?? false;
    setIsGift(giftSelected);
  }, [selectedPlan]);

  useEffect(() => {
    if (!stripe || !elements) return;

    const expressCheckoutElement = elements.getElement('expressCheckout');
    if (!expressCheckoutElement) return;

    const checkElementHeight = () => {
      const expressCheckoutDomElement = document.querySelector('#checkout-page .StripeElement');

      if (expressCheckoutDomElement) {
        const elementHeight = expressCheckoutDomElement.getBoundingClientRect().height;

        // If height is greater than a certain value (e.g., 20px), assume buttons are visible
        if (elementHeight > 20) {
          setExpressCheckoutVisible(true);
        }
      }
    };

    // Listen for the 'ready' event on the ExpressCheckoutElement
    expressCheckoutElement.on('ready', () => {
      let attempts = 0;
      const maxAttempts = 10;
      const interval = 50; // Check every 50ms

      const intervalId = setInterval(() => {
        attempts++;
        checkElementHeight();

        // Stop checking after a certain number of attempts
        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      }, interval);
    });

    return () => {
      expressCheckoutElement.off('ready');
    };
  }, [stripe, elements, cart?.totals?.total_price]);

  function decodeHtmlEntity(str) {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = str;
    return textArea.value;
  }

  const decodedCurrencySymbol = decodeHtmlEntity(currency_symbol);

  const onConfirm = async (event) => {
    if (!stripe) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const newErrors = {};
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Enter a valid email address';
      setErrors(newErrors);
      return;
    }

    try {

      let original_email;
      let isEmailDifferent = false;
      if (localStorage.getItem('email') && email !== localStorage.getItem('email')) {
        original_email = localStorage.getItem('email');
        localStorage.setItem('original_email', original_email);
        localStorage.setItem('email', email);
        isEmailDifferent = true;
        setSignupTriggered(false);
      } else if (!localStorage.getItem('email')) {
        localStorage.setItem('email', email);
      }

      let userSub = userSubExists;
      if(isEmailDifferent || !signupTriggered){
        let tf_uuid = localStorage.getItem('typeform_uuid');
        userSub = await sendSignUpEvent(email, original_email, tf_uuid);
        setSubExists(!userSub);
        userSub = !userSub;
      }

      if (userSub && !isGift && checkboxRef.current) {
        // Focus the checkbox input element
        checkboxRef.current.focus();
        return
      }

    } catch (error) {
      Sentry.captureException(error);
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log("Payment method error", error);
      setErrors(prevErrors => ({ ...prevErrors, order: 'Order prcessing failed, please try again' }));
      return;
    }

    // Create a PaymentMethod using the details collected by the Express Checkout Element
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          email: email
        }
      }
    });

    if (error) {
      // This point is only reached if there's an immediate error when
      // creating the PaymentMethod. Show the error to your customer (for example, payment details incomplete)
      console.log("Payment method error", error);
      setErrors(prevErrors => ({ ...prevErrors, order: 'Order prcessing failed, please try again' }));
      return
    }


    setLoading(true);
    setProcessingOrder(true);
    let billingInfo = {
      first_name: paymentMethod.billing_details?.name.split(" ")[0],
      last_name: paymentMethod.billing_details?.name.split(" ")[1],
      email: email,
      postcode: zipCode,
    }
    processOrder(billingInfo, paymentMethod.id, isGift)
      .then((res) => {
        if (res.status != 'pending') {
          if (!signupTriggered) triggerSignupEvents();
          window.location.href = res?.payment_result?.redirect_url;
        } else {
          setLoading(false);
          setProcessingOrder(false);
          setErrors(prevErrors => ({ ...prevErrors, order: 'Order Pending' }));
        }
      })
      .catch((err) => {
        console.log("ORDER PROCESS ERROR: ", err);
        setErrors(prevErrors => ({ ...prevErrors, order: 'Order prcessing failed, please try again' }));
        setLoading(false);
        setProcessingOrder(false);
      })

  };

  const expressCheckoutOptions = {
    buttonHeight: 50,
    wallets: {
      applePay: 'always',
      googlePay: 'always',
    },
    // // Specify a type per payment method
    // // Defaults to 'buy' for Google and 'plain' for Apple
    // buttonType: {
    //   googlePay: 'checkout',
    //   applePay: 'check-out'
    // },
    // // Specify a theme per payment method
    // // Default theme is based on appearance API settings
    // buttonTheme: {
    //   applePay: 'white-outline'
    // },
    // // Height in pixels. Defaults to 44. The width is always '100%'.
    // buttonHeight: 55
  }

  let validateTimeout;

  const validateFields = () => {
    const newErrors = {};
    if (cart?.totals?.total_price > 0 && !firstName.trim()) {
      newErrors.firstName = 'Enter first name';
    }
    if (cart?.totals?.total_price > 0 && !lastName.trim()) {
      newErrors.lastName = 'Enter last name';
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Enter a valid email address';
    }
    if (cart?.totals?.total_price > 0 && !cardComplete) {
      newErrors.card = 'Credit card details are incomplete';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCouponChange = (event) => {
    setCoupon(event.target.value);
  };

  const handleCardChange = (event) => {
    // Update card completion state based on whether the card element is empty
    setCardComplete(event.complete);
    if (checkErrors && !event.complete) { setErrors(prevErrors => ({ ...prevErrors, card: 'Credit card details are incomplete' })) }
    else { delete errors.card };
  };

  const applyCoupon = (code = null) => {
    // Logic to apply the coupon
    setShowSkeleton(true);
    applyCouponCode(code || coupon)
      .then(data => {
        setCart(data);
      })
      .catch(error => {
        console.error('Failed to fetch cart:', error);
        setErrors(prevErrors => ({ ...prevErrors, coupon: 'Invalid Coupon' }));
      })
      .finally(() => {
        setShowSkeleton(false);
      });
  };

  const removeCoupon = () => {
    setShowSkeleton(true);
    removeCouponCode(coupon)
      .then(data => {
        setCart(data);
      })
      .catch(error => {
        setErrors(prevErrors => ({ ...prevErrors, coupon: 'Error Removing Coupon' }));
      })
      .finally(() => {
        setShowSkeleton(false);
      });
  };

  const toggleGift = (event) => {
    const isChecked = event.target.checked;
    setIsGift(isChecked);
  };

  useEffect(() => {
    if (submitClicked) {
      setCheckErrors(true);
      if (validateFields()) {
        // All fields are valid, proceed with the form submission
        submitForm();
      } else {
        handleInvalidFields();
      }
      setSubmitClicked(false); // Reset the flag
    }
  }, [submitClicked, firstName, lastName]);

  const submitForm = async () => {
    if (!stripe || !elements) {
      console.log("Stripe.js has not loaded yet.");
      return;
    }

    console.log("Stripe OK");

    try {

      let original_email;
      let isEmailDifferent = false;
      if (localStorage.getItem('email') && email !== localStorage.getItem('email')) {
        original_email = localStorage.getItem('email');
        localStorage.setItem('original_email', original_email);
        localStorage.setItem('email', email);
        isEmailDifferent = true;
        setSignupTriggered(false);
      } else if (!localStorage.getItem('email')) {
        localStorage.setItem('email', email);
      }


      let userSub = userSubExists;
      if(isEmailDifferent || !signupTriggered){
        let tf_uuid = localStorage.getItem('typeform_uuid');
        userSub = await sendSignUpEvent(email, original_email, tf_uuid);
        setSubExists(!userSub);
        userSub = !userSub;
      }

      if (userSub && !isGift && checkboxRef.current) {
        // Focus the checkbox input element
        checkboxRef.current.focus();
        return
      }

    } catch (error) {
      console.log("error",error);
      Sentry.captureException(error);
      return;
    }

    if (cart?.totals?.total_price > 0) {

      const paymentMethodResult = await createStripePaymentMethod();
      if (!paymentMethodResult) return; // Early return if payment method creation fails

      setLoading(true); // Consider setting loading state at the beginning
      setProcessingOrder(true);

      await processStripeOrder(paymentMethodResult);
    }
    else {
      setLoading(true);
      setProcessingOrder(true);
      await processNonStripeOrder();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitClicked(true);

  };

  const handleInvalidFields = () => {
    console.log("INVALID FIELDS");
    if (!cardComplete) {
      setErrors(prevErrors => ({ ...prevErrors, card: 'Credit card details are incomplete' }));
    }
  };

  const createStripePaymentMethod = async () => {
    let paymentMethod;
    let paymentMethodResult;

    const billingDetails = {
      name: `${firstName} ${lastName}`,
      email: email,
    };

    try {
      if (usePaymentElement) {
        const { error: submitError } = await elements.submit();
        if (submitError) throw new Error('Order processing failed, please try again');

        paymentMethodResult = await stripe.createPaymentMethod({
          elements,
          params: { billing_details: billingDetails },
        });
      } else {
        const cardElement = elements.getElement(CardElement);
        paymentMethodResult = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: billingDetails,
        });
      }

      if (paymentMethodResult.error) throw new Error('Order processing failed, please try again');

      paymentMethod = paymentMethodResult.paymentMethod;
    } catch (error) {
      console.log('PAYMENT error', paymentMethodResult?.error || error);
      setErrors(prevErrors => ({ ...prevErrors, order: error.message }));
      setLoading(false);
      setProcessingOrder(false);
      return false;
    }

    return paymentMethod;
  };


  const triggerSignupEvents = () => {
    //Send signup event to VWO
    // if (localStorage.getItem('tracked_email') == email) return;

    const VWO = window.VWO;
    VWO.event("signup", {
      "email": email
    });

    window.dataLayer.push({
      event: 'gtm4wp.userRegistered',
      email: email,
      signup_type: 'email',
      visitorEmail: email
    });

    if (typeof window.Cookiebot !== 'undefined') {
      window.Cookiebot.submitCustomConsent(true, true, true);
      window.Cookiebot.hide()
    }

    setSignupTriggered(true);
  }

  const processStripeOrder = async (paymentMethod) => {
    let billingInfo = { first_name: firstName, last_name: lastName, email: email, postcode: zipCode };

    try {
      const res = await processOrder(billingInfo, paymentMethod.id, isGift);

      if (res.payment_result && res.payment_result.payment_details) {
        const paymentIntentSecret = res.payment_result.payment_details.find(detail => detail.key === "payment_intent_secret")?.value;
        const paymentStatus = res.payment_result.payment_status;
        const verificationEndpoint = res.payment_result.payment_details.find(detail => detail.key === "verification_endpoint")?.value;


        if (paymentStatus === 'success' && !paymentIntentSecret) {
          // If payment is already successful and no intent secret is provided, redirect the user
          window.location.href = res.payment_result.redirect_url;
          return;
        }

        if (paymentIntentSecret) {
          // Confirm the payment and handle 3DS authentication if required
          const result = await stripe.confirmCardPayment(paymentIntentSecret, {
            payment_method: paymentMethod.id
          });

          if (result.error) {
            // Handle errors during payment confirmation or 3DS authentication
            setErrors(prevErrors => ({ ...prevErrors, order: 'Order processing failed, please try again' }));
            setLoading(false);
            setProcessingOrder(false);
          } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
            // Payment successful
            if (!signupTriggered) triggerSignupEvents();
            if (verificationEndpoint) {
              const verificationRes = await fetch(verificationEndpoint);
              if (verificationRes.ok) {
                window.location.href = res.payment_result.redirect_url;
              } else {
                setErrors(prevErrors => ({ ...prevErrors, order: 'Order verification failed' }));
                setLoading(false);
                setProcessingOrder(false);
              }
            } else {
              window.location.href = res.payment_result.redirect_url;
            }
          } else {
            // Payment is pending or requires additional actions
            setLoading(false);
            setProcessingOrder(false);
            setErrors(prevErrors => ({ ...prevErrors, order: 'Order is pending approval' }));
          }
        } else {
          setErrors(prevErrors => ({ ...prevErrors, order: 'Order processing failed, payment intent secret is missing' }));
          setLoading(false);
          setProcessingOrder(false);
        }
      } else {
        setErrors(prevErrors => ({ ...prevErrors, order: 'Order processing failed, payment details are missing' }));
        setLoading(false);
        setProcessingOrder(false);
      }
    } catch (err) {
      console.log('Order processing failed', err);
      Sentry.captureException(err);
      const errorMessage = 'Order processing failed, please try again.';
      if (err.code === 'registration-error-email-exists') {
        errorMessage = 'An account is already registered with your email address.';
      }
      setErrors(prevErrors => ({ ...prevErrors, order: errorMessage }));
      setLoading(false);
      setProcessingOrder(false);
      updateNonce();
    }
  };


  const processNonStripeOrder = async (paymentMethod) => {
    let billingInfo = { email: email };

    try {
      const res = await processOrder(billingInfo, null, isGift);
      if (res.status != 'pending') {
        if (!signupTriggered) triggerSignupEvents();
        window.location.href = res?.payment_result?.redirect_url;
      } else {
        setLoading(false);
        setProcessingOrder(false);
        setErrors(prevErrors => ({ ...prevErrors, order: 'Order Pending' }));
      }
    } catch (err) {
      Sentry.captureException(err);
      // console.error("ORDER PROCESS ERROR: ", err);
      const errorMessage = 'Order processing failed, please try again.';
      if (err.code == 'registration-error-email-exists') {
        errorMessage = 'An account is already registered with your email address.'
      }
      setErrors(prevErrors => ({ ...prevErrors, order: errorMessage }));
      setLoading(false);
      setProcessingOrder(false);
      updateNonce();
    } finally {
    }
  };

  const formattedAmt = (amt) => {
    return (Math.round(amt * 100) / 100).toFixed(2)
  }

  // Helper function to render error messages
  const renderErrorMessage = (field) => {
    return errors[field] && <div className={styles.errorMsg}>{errors[field]}</div>;
  };

  const cardSectionRef = useRef(null);

  const toggleCardElementVisibility = () => {
    setIsCardElementVisible(prev => !prev);
  };

  // const toggleCardElementVisibility = () => {
  //   setIsCardElementVisible(prev => !prev);
  //   if (cardSectionRef.current && !isCardElementVisible) {
  //     cardSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //   }
  // };

  // Function to preload images
  const preloadImages = () => {
    const imageUrls = [
      "/wp-content/reactpress/apps/yc-checkout/build/assets/approved.webp",
      "/wp-content/reactpress/apps/yc-checkout/build/assets/shield.webp",
      "/wp-content/reactpress/apps/yc-checkout/build/assets/credit-card-icon.png",
      "/wp-content/reactpress/apps/yc-checkout/build/assets/secured-lock.svg"
      // Add URLs of other images to preload here
    ];

    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  };

  const checkboxRef = useRef(null);

  useEffect(() => {
    if (cart?.coupons?.length > 0) {
      setCouponApplied(true);
      setCoupon(cart?.coupons[0].code);
    }
    else {
      setCouponApplied(false);
      setCoupon('');
      setHasCoupon(false);
    }
  }, [cart]);

  function extractEmailFromResponse(response) {
    // Check if the response is valid and an array
    if (!Array.isArray(response) || response.length === 0) {
      return null;
    }

    // Extract the first response item
    const firstResponse = response[0];

    // If not found in 'hidden', check in 'answers'
    for (const answer of firstResponse.answers) {
      if (answer.type === 'email' && answer.email) {
        return answer.email;
      }
    }

    // Check in 'hidden' object
    if (firstResponse.hidden && firstResponse.hidden.email) {
      return firstResponse.hidden.email;
    }

    // Return null if no email found
    return null;
  }

  useEffect(() => {
    preloadImages();

    let local_email = localStorage.getItem("email");
    if (!!local_email) { setEmail(local_email); };

  }, []);

  useEffect(() => {
    if (initCart) {
      const queryParams = new URLSearchParams(window.location.search);
      const code = queryParams.get('coupon') || get_cookie('coupon');
      if (code) {
        applyCoupon(code);
      }
    }
  }, [initCart]);

  return (
    <>{loading == true ? <Loader /> :
      <form onSubmit={handleSubmit} className={styles.paymentForm}>
        <div className={styles.summaryCol}>
          <div className={styles.summaryColContent}>
          <h3>Order Summary</h3>
          <div className={styles.checkoutSummarySection}>
            {!!cart?.items ? cart?.items.map((item, index) => (
              <div key={index} className={styles.lineItem}>
                <span className={styles.text}>{selectedPlan?.title}</span>
                {showSkeleton == true ? <div className={styles.skeleton}></div> :
                  <>{(item.totals?.line_subtotal !== item.prices?.price) && <span className={styles.saveAmount}>{decodedCurrencySymbol}{item.prices?.price / 100}</span>}
                    <span className={styles.amount}>{decodedCurrencySymbol}{formattedAmt(item.totals?.line_subtotal / 100)}</span>
                  </>}
              </div>
            )) : (<> <div className={styles.lineItem}><span className={styles.text}>{selectedPlan?.title}</span><div className={styles.skeleton}></div></div></>)}
            {cart?.discounts?.special_offer && (<div className={styles.lineItem}>
              <span className={styles.text}>{cart?.discounts?.special_offer?.label}</span>
              {/* <span className={styles.saveAmount}>Save $204</span> */}
              <span className={styles.amount}>-{decodedCurrencySymbol}{formattedAmt(cart?.discounts?.special_offer?.discount / 100)}</span>
            </div>)}
            {selectedPlan?.bogo && <div className={styles.lineItem}>
                <span className={styles.text}>1 free gift membership</span>
                {showSkeleton && <div className={styles.skeleton}></div>}
                {!showSkeleton && <>
                  <span className={styles.saveAmount}>{decodedCurrencySymbol}{selectedPlan?.price}</span>
                  <span className={styles.amount}>{decodedCurrencySymbol}{formattedAmt(0)}</span>
                </>}
            </div>}
            {(cart?.coupons?.length > 0) && (
              <div className={styles.lineItem}>
                <span className={styles.text}>Coupon: {cart?.coupons[0]?.code}
                  <span className={styles.removeCoupon} onClick={removeCoupon}>x</span>
                </span>
                {/* <span className={styles.saveAmount}>Save $204</span> */}
                {showSkeleton == true ? <div className={styles.skeleton}></div> :
                  <span className={styles.amount}>-{decodedCurrencySymbol}{formattedAmt(cart?.coupons[0]?.totals?.total_discount / 100)}</span>
                }
              </div>)}
            {cart?.coupons?.length == 0 && <div className={styles.lineItem}>
              <span className={`${styles.text} ${styles.couponText}`} onClick={() => setHasCoupon(!hasCoupon)} >Coupon?</span>
            </div>}
            {hasCoupon && !couponApplied && <><div className={styles.couponSection}>
              <input
                type="text"
                id="coupon"
                value={coupon}
                onChange={handleCouponChange}
                placeholder="Enter your coupon"
                className={styles.couponInput}
              />
              <button type='button' onClick={() => applyCoupon()} className={styles.applyButton}>APPLY</button>
            </div>
              {renderErrorMessage('coupon')}</>
            }
            <div className={styles.lineDivider}></div>
            <div className={styles.totalBilled}>
              <span className={styles.totalBilledTitle}>Total billed today</span>
              {showSkeleton == true ? <div className={styles.skeleton}></div> :
                <span className={styles.totalBilledAmount}>{decodedCurrencySymbol}{formattedAmt(cart?.totals?.total_price / 100)}</span>
              }
            </div>
            {selectedPlan?.showSavings && <div className={`${styles.savingsBanner}`}>
              {!showSkeleton && (
                <><span className={styles.triangleRight}></span><span>
                  You’re saving {decodedCurrencySymbol}
                  {Math.round(
                    parseFloat(selectedPlan?.saving_amount || 0)
                  )} today
                </span><span className={styles.triangleLeft}></span></>
              )}
            </div>}
            {!!selectedPlan?.checkout_note && <><span className={styles.billingInfo}>
              {selectedPlan.checkout_note}
            </span><div className={styles.lineDivider}></div></>
            }
            <div className={`${styles.giftOption} ${subExists && !isGift ? styles.focusCheckbox : ''}`}>
              <label className={styles.giftCheckboxContainer}>
                <input
                  ref={checkboxRef}
                  type="checkbox"
                  checked={isGift}
                  onChange={toggleGift}
                  disabled={selectedPlan?.bogo} // disable the checkbox if the selected plan is bogo
                />
                <span><strong>Gift it </strong>(you’ll get a gift card to your email)</span>
              </label>
            </div>
          </div>
          <section className={styles.guaranteeSection}>
            <div className={styles.guaranteeContainer}>
              <div className={styles.guaranteeImg}><img src="https://static.yeschef.me/2025/03/padlock_32_green.png" alt="Secure Checkout" className={styles.shieldImg} /></div>
              <div>
                <h4>Secure Checkout</h4>
                <p>Your payment information is fully protected.</p>
              </div>
            </div>
          </section>
          </div>
        </div>

        <div className={styles.paymentsColWrapper}>
          <div className={styles.paymentsCol}>
          <h3>Payment Details</h3>
          <div className={`${styles.inputSection} ${styles.emailTop}`}><div className={styles.inputContainer}>
            {/* <label className={styles.inputLabel}>Email Address</label> */}
            <input
              type="text"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearTimeout(validateTimeout);
                if (checkErrors) validateTimeout = setTimeout(() => validateFields(), 800);
              }}
              placeholder="Enter your email address"
              className={`${styles.inputField} ${errors.email ? styles.inputError : ''}`} />
            {renderErrorMessage('email')}
          </div></div>
          <div className={styles.privacyText}>By becoming a member you accept our <a target="_blank" href='https://yeschef.me/terms/'>Terms</a> and <a target="_blank" href='https://yeschef.me/privacy/'>Privacy Policy</a>.</div>
          {subExists && !isGift && <div className={styles.subError}>You already have an active YesChef membership. Did you mean to gift it?</div>}
          {cart?.totals?.total_price > 0 ?
            (<><div id="checkout-page" className={styles.expressElement}>
              <ExpressCheckoutElement
                ref={expressElementRef}
                options={expressCheckoutOptions}
                onConfirm={onConfirm} />
            </div>
              {isExpressCheckoutVisible && (
                <div className={styles.orDividerContainer}><div className={styles.orDivider}></div>
                  <div className={styles.orDividerText}>OR</div>
                  <div className={styles.orDivider}></div></div>
              )}
              <div className={styles.cardSection} ref={cardSectionRef}>
                <div
                  className={styles.cardHeader}
                >
                  <img src="/wp-content/reactpress/apps/yc-checkout/build/assets/credit-card-icon.png" alt="Credit Card" className={styles.creditCardImg}></img>
                  <span className={styles.cardText}>Credit Card</span>
                  {/* <span className={styles.toggleArrow} style={{ transform: isCardElementVisible ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="9" viewBox="0 0 15 9" fill="none">
                      <path d="M1 1L6.73735 7.17869C7.14376 7.61636 7.84071 7.60251 8.2294 7.14903L13.5 1" stroke="#1E1E1E" stroke-width="2" />
                    </svg>
                  </span> */}
                </div>

                {isCardElementVisible && (
                  <>
                    <div className={styles.cardDetails}>
                      <div className={styles.inputSection}>
                        <div className={styles.inputGroup}>
                          <div className={styles.inputContainer}>
                            <input
                              type="text"
                              value={firstName}
                              onChange={(e) => {
                                setFirstName(e.target.value);
                                clearTimeout(validateTimeout);
                                if (checkErrors) validateTimeout = setTimeout(() => validateFields(), 800);
                              }}
                              placeholder="First name"
                              className={`${styles.inputField} ${errors.firstName ? styles.inputError : ''}`} />
                            {renderErrorMessage('firstName')}
                          </div>

                          <div className={styles.inputContainer}>
                            <input
                              type="text"
                              value={lastName}
                              onChange={(e) => {
                                setLastName(e.target.value);
                                clearTimeout(validateTimeout);
                                if (checkErrors) validateTimeout = setTimeout(() => validateFields(), 800);
                              }}
                              placeholder="Last name"
                              className={`${styles.inputField} ${errors.lastName ? styles.inputError : ''}`} />
                            {renderErrorMessage('lastName')}
                          </div>
                        </div>

                        <div className={`${styles.cardElementContainer}`}>
                          {/* <label className={styles.inputLabel}>Card Details</label> */}

                          {usePaymentElement ? (<PaymentElement
                            onChange={handleCardChange}
                          />) : (
                            <CardElement className={`${styles.cardElement} ${errors.card ? styles.inputError : ''}`} options={{ style: { base: { fontFamily: 'Open Sans', fontSize: '16px', fontWeight: '600', '::placeholder': { color: '#9c9c9c', fontSize: '16px', fontWeight: '600' }, } }, hidePostalCode: false, disableLink: true }} onChange={handleCardChange} />
                          )}
                          {renderErrorMessage('card')}
                        </div>

                      </div>
                      <button type="submit" disabled={!stripe || showSkeleton} className={styles.placeOrderButton}>
                        <img src="/wp-content/reactpress/apps/yc-checkout/build/assets/secured-lock.svg" alt="Secure" className={styles.lockdImg}></img>
                        PLACE SECURE ORDER
                      </button>
                      {renderErrorMessage('order')}
                      <div className={styles.secureCheckout}>
                        {/* <span role="img" aria-label="Secure">🔒</span> */}
                        <div className={styles.secureImg}><img src="/wp-content/reactpress/apps/yc-checkout/build/assets/approved.webp" alt="Checkmark" className={styles.approvedImg} /></div>
                        <div className={styles.secureText}>
                          <h4>Secure checkout</h4>
                          <span className={styles.divider}>|</span>
                          <p>Your payment information is fully protected.</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div></>)
            :
            (
              !showSkeleton && (
                <>
                  <button
                    type="submit"
                    disabled={!stripe || showSkeleton}
                    className={`${styles.placeOrderButton} ${styles.bottomButton}`}
                  >
                    <img
                      src="/wp-content/reactpress/apps/yc-checkout/build/assets/secured-lock.svg"
                      alt="Secure"
                      className={styles.lockdImg}
                    />
                    PLACE SECURE ORDER
                  </button>
                  {renderErrorMessage('order')}
                </>
              )
            )}
        </div>
        </div>
      </form>
    }
    </>
  );
};

export default PaymentForm;